/**
 * BrandPreview — live 10-shade preview for the admin company-branding form.
 *
 * Why: `applyBranding` only fires after the admin saves and reloads, which is
 * a slow feedback loop for "does my brand colour actually look right?". This
 * component derives the same 10-shade palette client-side via
 * `@mantine/colors-generator` (the same algorithm the backend uses on save)
 * and scopes a `--lex-brand-{0..9}` override to a wrapper div. Mantine's
 * resolver then resolves `--mantine-color-brand-N` against the scoped brand
 * tokens, so the sample button / badge / alert / input render with the
 * preview palette without affecting the surrounding admin page.
 *
 * Production-side guarantee: the preview's `generateColors` import matches
 * the backend's, so what the admin sees here is what the live tenant gets
 * post-save.
 */

import { Trans } from "@lingui/react/macro"
import { generateColors } from "@mantine/colors-generator"
import { Alert, Badge, Button, Card, ColorSwatch, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import type { CSSProperties } from "react"
import { useMemo } from "react"
import type { IProps } from "./interfaces"

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function isValidHex(value: string): boolean {
  return HEX_RE.test(value.trim())
}

export function BrandPreview({ brandColor }: IProps): React.JSX.Element {
  const palette = useMemo<string[] | null>(() => {
    if (!isValidHex(brandColor)) return null
    try {
      return [...generateColors(brandColor.trim())]
    } catch {
      return null
    }
  }, [brandColor])

  if (!palette) {
    return (
      <Text size="sm" c="dimmed">
        <Trans>Enter a valid hex colour to preview the derived palette.</Trans>
      </Text>
    )
  }

  // Scope the brand override to this subtree only — admin chrome around
  // BrandPreview keeps its default palette.
  //
  // We have to override `--mantine-color-brand-{0..9}` AND the slot tokens
  // (`--mantine-primary-color-filled` etc.) DIRECTLY — not just the Layer 1.5
  // `--lex-brand-N` alias. Mantine's `cssVariablesResolver` returns
  // `var(--lex-brand-N)` as a string, but Mantine resolves it to the literal
  // hex at theme construction and emits that into the document <style> tag.
  // So overriding the alias at a wrapper has no effect — the resolved hex is
  // already baked at root scope. We override the resolved Mantine slots
  // directly to make the preview reflect what production will look like
  // post-save (where applyBranding does the same trick at <html>).
  const styleOverride: Record<string, string> = {}
  palette.forEach((hex, i) => {
    styleOverride[`--lex-brand-${i}`] = hex
    styleOverride[`--mantine-color-brand-${i}`] = hex
  })
  // Mantine derives a family of filled / light / outline slot tokens from the
  // brand palette and emits resolved hexes for them at theme construction
  // time. `color="brand"` consumers read from these slots (NOT from the
  // -0..-9 ramp directly), so we re-derive each one at the same indices the
  // resolver uses. Index map:
  //   filled         → 7      (the dark, button-fill shade)
  //   filled-hover   → 8
  //   light          → 0       (translucent fill backing)
  //   light-hover    → 1
  //   light-color    → 4       (text on the light fill)
  //   outline        → 4       (border + text on outline buttons)
  //   outline-hover  → 0       (subtle fill on hover)
  const filled = palette[7] ?? ""
  const filledHover = palette[8] ?? ""
  const light = palette[0] ?? ""
  const lightHover = palette[1] ?? ""
  const lightColor = palette[4] ?? ""
  const outline = palette[4] ?? ""
  const outlineHover = palette[0] ?? ""
  styleOverride["--mantine-color-brand-filled"] = filled
  styleOverride["--mantine-color-brand-filled-hover"] = filledHover
  styleOverride["--mantine-color-brand-light"] = light
  styleOverride["--mantine-color-brand-light-hover"] = lightHover
  styleOverride["--mantine-color-brand-light-color"] = lightColor
  styleOverride["--mantine-color-brand-outline"] = outline
  styleOverride["--mantine-color-brand-outline-hover"] = outlineHover
  styleOverride["--mantine-primary-color-filled"] = filled
  styleOverride["--mantine-primary-color-filled-hover"] = filledHover

  return (
    <Stack gap="md" style={styleOverride as CSSProperties}>
      <Group gap={6}>
        {palette.map((hex, i) => (
          <Tooltip key={hex + i} label={`brand-${i}: ${hex}`} withArrow position="top">
            <ColorSwatch color={hex} size={28} radius="sm" style={{ cursor: "help" }} />
          </Tooltip>
        ))}
      </Group>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group gap="sm">
            <Button color="brand">
              <Trans>Primary</Trans>
            </Button>
            <Button color="brand" variant="light">
              <Trans>Light</Trans>
            </Button>
            <Button color="brand" variant="outline">
              <Trans>Outline</Trans>
            </Button>
            <Badge color="brand" size="lg">
              <Trans>Badge</Trans>
            </Badge>
          </Group>
          <Alert color="brand" title={<Trans>Heads up</Trans>}>
            <Trans>This alert renders against the live brand palette.</Trans>
          </Alert>
          <TextInput placeholder="Focus me to see the brand focus ring" />
        </Stack>
      </Card>
    </Stack>
  )
}
